import {
  Box,
  Button,
  DialogActions,
  InputAdornment,
  InputLabelProps,
  TextField,
} from "@mui/material";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import React, { useState } from "react";
import { RhfProps } from "./types/RhfProps";
import { RhfStyleProps } from "./types/RhfStyleProps";
import CalendarIcon from "@mui/icons-material/Event";
import ClockIcon from "@mui/icons-material/Schedule";

import { Controller } from "react-hook-form";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { PickersActionBarProps } from "@mui/x-date-pickers/PickersActionBar";
import { CalendarOrClockPickerView } from "@mui/x-date-pickers/internals/models";
import { useTranslation } from "react-i18next";

type Props = RhfProps &
  RhfStyleProps & {
    label?: React.ReactNode;
    size?: "small" | "medium" | undefined;

    /** 是否顯示清空按鈕，並將數值設為 null */
    allowNull?: boolean;

    /** 日期格式 預設:"YYYY/MM/DD" */
    inputFormat?: string;

    /** react-hook-form 寫入數值時要如何轉換日期為字串 */
    rhfValueFormat?: string;

    /** 是否顯示 日期 或 時間 選擇器: 預設自動判斷，但也可以手動指定 */
    views?: readonly CalendarOrClockPickerView[];

    showErrorText?: boolean;

    InputLabelProps?: InputLabelProps;

    disabled?: boolean | undefined;
  };

/**
 * 日期/時間 選擇器
 */
const RhfDateTimePicker = ({
  rhf,
  name,
  defaultValue,
  rules, // RhfProps
  sx,
  className,
  style, // RhfStyleProps
  label,
  size,

  allowNull = true,

  inputFormat,
  views,

  rhfValueFormat,

  showErrorText,

  InputLabelProps,

  disabled,
  ...restProps
}: Props) => {
  const [IsOpen, setIsOpen] = useState<boolean>(false);
  const { t, i18n } = useTranslation();

  console.log(i18n.language);

  if (inputFormat == null) {
    inputFormat = "YYYY/MM/DD";
  }

  let { view_array, has_date, has_time, has_ampm } =
    GetDefaultViews(inputFormat);

  // 不特別指定時，只有日期沒有時間，則省略時間部分
  if (rhfValueFormat == null) {
    rhfValueFormat = "";
    rhfValueFormat += (inputFormat.includes("YYYY") ? "YYYY" : "2000") + "-"; // 有日期則應該有年 2000年2月有29日 但是建議還是要填年
    rhfValueFormat += (inputFormat.includes("MM") ? "MM" : "01") + "-"; // 沒有月份 則月份為01
    rhfValueFormat += inputFormat.includes("DD") ? "DD" : "01"; // 沒有日期 則日期為01
    rhfValueFormat += " ";
    rhfValueFormat +=
      (inputFormat.includes("HH") || inputFormat.includes("hh") ? "HH" : "00") +
      ":";
    rhfValueFormat += (inputFormat.includes("mm") ? "mm" : "00") + ":";
    rhfValueFormat += inputFormat.includes("ss") ? "ss" : "00";
  }

  if (showErrorText == null) {
    showErrorText = true; // 預設顯示錯誤訊息
  }

  return (
    <Controller
      control={rhf?.control}
      name={name}
      defaultValue={defaultValue == null ? null : String(defaultValue)}
      rules={rules}
      render={({ field: { onChange, value, ...restField }, fieldState }) => {
        // 等同於: render={({ field: { onChange, onBlur, value, ref } }) => (

        const has_error: boolean = !!fieldState.error;

        return (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={i18n.language === "fil" ? "tl-ph" : i18n.language}
          >
            <MobileDateTimePicker
              inputFormat={inputFormat} /* 預設: YYYY/MM/DD */
              hideTabs={
                !(has_date && has_time)
              } /* 有日期，有時間，這時才完整顯示，否則隱藏 */
              views={view_array} // 預設可選順序選擇 年 月 日 時 分 秒
              ampm={has_ampm}
              disabled={disabled}
              toolbarFormat="MM/DD" // 選擇器顯示日期格式
              components={{
                ActionBar: render_ActionButtons,
              }}
              onOpen={() => {
                setIsOpen(true);
              }} // 記憶Dialog開啟 (TextBox 可輸入)
              onClose={() => {
                setIsOpen(false);
              }} // 記憶Dialog開啟 (TextBox 唯讀)
              toolbarTitle={<div>&nbsp;</div>} // 乾脆不顯示 label
              label={label}
              value={value}
              onChange={(newValue) => {
                const str_val =
                  newValue == null
                    ? null
                    : (newValue as Dayjs).format(rhfValueFormat);
                onChange(str_val);
              }}
              InputProps={{
                // 依照是否有時間來顯示 月曆 或 時鐘 圖示
                endAdornment: (
                  <InputAdornment position="end">
                    {has_date === false ? (
                      <ClockIcon color={disabled ? "disabled" : undefined} />
                    ) : (
                      <CalendarIcon color={disabled ? "disabled" : undefined} />
                    )}
                  </InputAdornment>
                ),
              }}
              renderInput={(params) => (
                <TextField
                  InputLabelProps={InputLabelProps}
                  sx={{
                    ...{
                      minWidth:
                        sx == null || (sx as any).width == null
                          ? "256px"
                          : undefined,
                    }, // 沒設定寬度時給一個最小寬度
                    ...sx,
                  }}
                  className={className}
                  style={style}
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: IsOpen === false,
                  }}
                  size={size}
                  error={params.error || (showErrorText && has_error)}
                  helperText={
                    showErrorText && has_error
                      ? t(fieldState.error?.message ?? "")
                      : undefined
                  }
                  {...restField}
                />
              )}
            />
          </LocalizationProvider>
        );
      }}
    />
  );

  /* 自定義最下面一排按鈕 */
  function render_ActionButtons(props: PickersActionBarProps) {
    const { onAccept, onClear, onCancel, onSetToday, actions } = props;
    const sx_small_button = {
      m: 0,
      p: 0,
      ml: "8px",
      minWidth: "48px",
      color: "text.secondary",
    };

    return (
      <DialogActions>
        <Button size="small" onClick={onSetToday} sx={sx_small_button}>
          {" "}
          {has_time ? t("現在") : t("今天")}{" "}
        </Button>
        {allowNull == null || allowNull === false ? undefined : (
          <Button size="small" onClick={onClear} sx={sx_small_button}>
            {" "}
            {t("清空")}{" "}
          </Button>
        )}
        <Box flex={1}></Box>
        <Button size="small" onClick={onCancel} sx={sx_small_button}>
          {" "}
          {t("取消")}{" "}
        </Button>
        <Button onClick={onAccept}> {t("確定")} </Button>
      </DialogActions>
    );

    // 其他自定義方式
    // https://stackoverflow.com/questions/72602586/how-to-change-accept-and-cancel-buttons-text-for-the-mobiledatepicker-component
  }

  /** 依照輸入的Format自動產生對應樣式 */
  function GetDefaultViews(inputFormat: string): {
    view_array: readonly CalendarOrClockPickerView[];
    has_date: boolean;
    has_time: boolean;
    has_ampm: boolean;
  } {
    let has_date = false;
    let has_time = false;
    let has_ampm = true;

    let view_array: CalendarOrClockPickerView[] = [];

    if (views == null) {
      if (inputFormat.includes("YYYY")) {
        view_array.push("year");
        has_date = true;
      }

      if (inputFormat.includes("MM")) {
        view_array.push("month");
        has_date = true;
      }

      if (inputFormat.includes("DD")) {
        view_array.push("day");
        has_date = true;
      }

      if (inputFormat.includes("HH") || inputFormat.includes("hh")) {
        if (inputFormat.includes("HH")) {
          has_ampm = false;
        }

        view_array.push("hours");
        has_time = true;
      }

      if (inputFormat.includes("mm")) {
        view_array.push("minutes");
        has_time = true;
      }

      if (inputFormat.includes("ss")) {
        view_array.push("seconds");
        has_time = true;
      }

      return { view_array, has_date, has_time, has_ampm };
    } else {
      return { view_array: views, has_date, has_time, has_ampm };
    }
  }
};

export default RhfDateTimePicker;
